/**
 * Countdown Screen Component
 * Shows "Get ready for Question X" with a 3-2-1 countdown
 * Displayed before each question starts
 */

import React, { useEffect, useRef } from "react";
import { View, Text, Animated, Easing, Dimensions, StyleSheet } from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import LottieView from "lottie-react-native";
import { useThemeColors } from "@/providers/ThemeProvider";
import { getLottieSource } from "@/lib/lottie-registry";

const { width: SCREEN_WIDTH } = Dimensions.get("window");

interface CountdownScreenProps {
  /** Current question number (1-indexed) */
  questionNumber: number;
  /** Total number of questions */
  totalQuestions: number;
  /** Seconds remaining in countdown (3, 2, 1, 0) */
  secondsRemaining: number;
  /** Quiz name for display */
  quizName?: string;
  /** Quiz theme color */
  quizColor?: string;
}

/**
 * Countdown Screen - "Get ready" animation before each question
 */
export const CountdownScreen: React.FC<CountdownScreenProps> = ({
  questionNumber,
  totalQuestions,
  secondsRemaining,
  quizName,
  quizColor = "#8B5CF6",
}) => {
  const colors = useThemeColors();
  
  // Animations
  const scaleAnim = useRef(new Animated.Value(0.5)).current;
  const opacityAnim = useRef(new Animated.Value(0)).current;
  const pulseAnim = useRef(new Animated.Value(1)).current;
  
  // Animate when countdown changes
  useEffect(() => {
    // Reset animations
    scaleAnim.setValue(0.5);
    opacityAnim.setValue(0);
    
    // Pop-in animation for the number
    Animated.parallel([
      Animated.spring(scaleAnim, {
        toValue: 1,
        tension: 100,
        friction: 8,
        useNativeDriver: true,
      }),
      Animated.timing(opacityAnim, {
        toValue: 1,
        duration: 150,
        useNativeDriver: true,
      }),
    ]).start();
    
    // Pulse animation
    Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnim, {
          toValue: 1.1,
          duration: 300,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(pulseAnim, {
          toValue: 1,
          duration: 300,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ])
    ).start();
  }, [secondsRemaining, scaleAnim, opacityAnim, pulseAnim]);
  
  /**
   * Get color for countdown number based on remaining seconds
   */
  const getCountdownColor = () => {
    if (secondsRemaining === 3) return "#10B981"; // Green
    if (secondsRemaining === 2) return "#F59E0B"; // Yellow
    if (secondsRemaining === 1) return "#EF4444"; // Red
    return quizColor; // GO color
  };
  
  /**
   * Get display text for countdown
   */
  const getCountdownDisplay = () => {
    if (secondsRemaining <= 0) return "GO!";
    return secondsRemaining.toString();
  };
  
  // Get Lottie source for rocket/celebration
  const rocketLottie = getLottieSource("rocket");
  const starLottie = getLottieSource("star_struck");
  
  return (
    <LinearGradient
      colors={[quizColor, "#4F46E5", "#1e1b4b"]}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
      style={styles.container}
    >
      {/* Background decorations */}
      <View style={styles.decorationContainer}>
        {rocketLottie && (
          <LottieView
            source={rocketLottie}
            autoPlay
            loop
            style={styles.decorationLottie}
          />
        )}
      </View>
      
      {/* Question number indicator */}
      <View style={styles.questionIndicator}>
        <Text style={styles.questionText}>
          Vraag {questionNumber} van {totalQuestions}
        </Text>
        {quizName && (
          <Text style={styles.quizNameText}>{quizName}</Text>
        )}
      </View>
      
      {/* "Get Ready" text */}
      <Animated.Text
        style={[
          styles.getReadyText,
          {
            opacity: opacityAnim,
            transform: [{ scale: scaleAnim }],
          },
        ]}
      >
        {secondsRemaining <= 0 ? "DAAR GAAN WE!" : "Klaar voor..."}
      </Animated.Text>
      
      {/* Big countdown number */}
      <Animated.View
        style={[
          styles.countdownCircle,
          {
            backgroundColor: getCountdownColor(),
            transform: [
              { scale: Animated.multiply(scaleAnim, pulseAnim) },
            ],
            opacity: opacityAnim,
          },
        ]}
      >
        <Text style={styles.countdownNumber}>
          {getCountdownDisplay()}
        </Text>
      </Animated.View>
      
      {/* Celebration Lottie for "GO!" */}
      {secondsRemaining <= 0 && starLottie && (
        <View style={styles.celebrationContainer}>
          <LottieView
            source={starLottie}
            autoPlay
            loop={false}
            style={styles.celebrationLottie}
          />
        </View>
      )}
      
      {/* Encouraging message */}
      <Animated.Text
        style={[
          styles.encouragementText,
          { opacity: opacityAnim },
        ]}
      >
        {secondsRemaining <= 0 
          ? "🎯 Succes!" 
          : `💪 Vraag ${questionNumber} komt eraan!`
        }
      </Animated.Text>
    </LinearGradient>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  decorationContainer: {
    position: "absolute",
    top: 40,
    right: 40,
    opacity: 0.3,
  },
  decorationLottie: {
    width: 150,
    height: 150,
  },
  questionIndicator: {
    position: "absolute",
    top: 60,
    left: 0,
    right: 0,
    alignItems: "center",
  },
  questionText: {
    color: "rgba(255,255,255,0.9)",
    fontSize: 24,
    fontWeight: "600",
  },
  quizNameText: {
    color: "rgba(255,255,255,0.7)",
    fontSize: 18,
    marginTop: 8,
  },
  getReadyText: {
    color: "#FFFFFF",
    fontSize: 32,
    fontWeight: "bold",
    marginBottom: 30,
    textShadowColor: "rgba(0,0,0,0.3)",
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  countdownCircle: {
    width: SCREEN_WIDTH * 0.5,
    height: SCREEN_WIDTH * 0.5,
    maxWidth: 300,
    maxHeight: 300,
    borderRadius: 999,
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.3,
    shadowRadius: 20,
    elevation: 10,
  },
  countdownNumber: {
    color: "#FFFFFF",
    fontSize: 120,
    fontWeight: "900",
    textShadowColor: "rgba(0,0,0,0.2)",
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  celebrationContainer: {
    position: "absolute",
    top: "30%",
    left: 0,
    right: 0,
    alignItems: "center",
  },
  celebrationLottie: {
    width: 300,
    height: 300,
  },
  encouragementText: {
    color: "rgba(255,255,255,0.9)",
    fontSize: 24,
    fontWeight: "600",
    marginTop: 40,
    textAlign: "center",
  },
});

export default CountdownScreen;

